import { GeomT } from "models/model";

export interface Bin {
  label: string;
  min?: number; // For numeric bins
  max?: number; // For numeric bins
  features: GeomT[];
  color?: string;
  isLessThan?: boolean; // True if this is a "< value" bin
  isGreaterThan?: boolean; // True if this is a "> value" bin
  isNA?: boolean; // True if this is a "N/A" bin for null/undefined values
}

export interface BinningResult {
  bins: Bin[];
  method: string;
  property: string;
}
export enum BinningMethod {
  BinSize = "binSize",
  Quantile = "quantile"
}

export interface BinSettings {
  property: string;
  method: BinningMethod;
  // For binSize method:
  lessThan?: number | null;
  binSize?: number | null;
  greaterThan?: number | null;
  // For quantile method:
  quantileCount?: number;
}

/**
 * Get the number of decimal places in a number
 */
function getDecimalPlaces(value: number): number {
  if (Number.isInteger(value)) return 0;
  const str = value.toString();
  const decimalIndex = str.indexOf(".");
  if (decimalIndex === -1) return 0;
  return str.length - decimalIndex - 1;
}

/**
 * Format a number for display with appropriate decimal places
 * @param value - The number to format
 * @param decimalPlaces - Number of decimal places (if undefined, uses minimal representation)
 */
function formatNumber(value: number | undefined, decimalPlaces?: number): string {
  if (value === undefined) return "";

  if (decimalPlaces === undefined) {
    // Use minimal representation - remove trailing zeros
    if (Number.isInteger(value)) return value.toString();
    return value.toString();
  }

  // Use specified decimal places
  if (decimalPlaces === 0) return Math.round(value).toString();
  return value.toFixed(decimalPlaces);
}

/**
 * Check if a value should be treated as N/A
 */
function isNAValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") {
    return true;
  }
  if (typeof value === "string" && value.trim().toLowerCase() === "n/a") {
    return true;
  }
  return false;
}

export function isNumericProperty(features: GeomT[], property: string): boolean {
  if (!features?.length || !property) return false;

  let hasValidNumericValue = false;

  for (const feature of features) {
    const value = feature.Properties?.[property];

    if (value === null || value === undefined || value === "") {
      continue;
    }

    if (typeof value === "string" && value.trim().toLowerCase() === "n/a") {
      continue;
    }

    const num = typeof value === "number" ? value : Number(value);

    if (Number.isFinite(num)) {
      hasValidNumericValue = true;
    } else {
      return false;
    }
  }

  return hasValidNumericValue;
}

const EPSILON = 1e-10;

/**
 * Generate all possible bins based on binning parameters
 */
function getAllPossibleBins(
  lessThan: number,
  binSize: number,
  greaterThan: number,
  isIntegerBinning: boolean,
  decimalPlaces: number
): Bin[] {
  const bins: Bin[] = [];

  // Safety checks to prevent infinite loops
  if (binSize <= 0 || lessThan >= greaterThan) {
    return bins;
  }

  // Add "less than" bin
  bins.push({
    label: `< ${formatNumber(lessThan, decimalPlaces)}`,
    min: undefined,
    max: lessThan,
    features: [],
    isLessThan: true
  });

  // Generate regular bins starting from lessThan
  let i = lessThan;
  while (i < greaterThan) {
    const binMin = i;
    // Cap the bin max at greaterThan to ensure last bin doesn't exceed it
    const binMax = Math.min(i + binSize, greaterThan);

    // Format label - always show as "min - max" range
    const label = `${formatNumber(binMin, decimalPlaces)} - ${formatNumber(
      binMax,
      decimalPlaces
    )}`;

    bins.push({
      label,
      min: binMin,
      max: binMax,
      features: []
    });

    i += binSize;
  }

  // Add "greater than" bin
  bins.push({
    label: `≥ ${formatNumber(greaterThan, decimalPlaces)}`,
    min: greaterThan,
    max: undefined,
    features: [],
    isGreaterThan: true
  });

  return bins;
}

/**
 * Assign features to their appropriate bins
 */
function assignFeaturesToBins(features: GeomT[], property: string, bins: Bin[]): Bin[] {
  // Create n/a bin for non-numeric values (not added to bins array yet)
  const naBin: Bin = {
    label: "n/a",
    features: [],
    isNA: true
  };

  for (const feature of features) {
    const rawValue = feature.Properties?.[property];

    // Check if this is an N/A value
    if (isNAValue(rawValue)) {
      naBin.features.push(feature);
      continue;
    }

    const value = Number(rawValue);
    if (Number.isNaN(value)) {
      naBin.features.push(feature);
      continue;
    }

    // Find the appropriate bin for this value
    let assigned = false;
    for (const bin of bins) {
      // Skip N/A bin during numeric assignment
      if (bin.isNA) continue;

      // Handle less-than bin (value < max)
      if (bin.isLessThan && bin.max !== undefined && value < bin.max) {
        bin.features.push(feature);
        assigned = true;
        break;
      }

      // Handle greater-than bin (value >= min, inclusive)
      if (bin.isGreaterThan && bin.min !== undefined && value >= bin.min) {
        bin.features.push(feature);
        assigned = true;
        break;
      }

      // Handle regular bins: left-inclusive, right-exclusive [min, max)
      if (
        !bin.isLessThan &&
        !bin.isGreaterThan &&
        bin.min !== undefined &&
        bin.max !== undefined &&
        value >= bin.min &&
        value < bin.max
      ) {
        bin.features.push(feature);
        assigned = true;
        break;
      }
    }

    if (!assigned && bins.length > 0) {
      const lastRegularBin = bins
        .filter((b) => !b.isLessThan && !b.isGreaterThan && !b.isNA)
        .pop();
      if (
        lastRegularBin &&
        lastRegularBin.max !== undefined &&
        value === lastRegularBin.max
      ) {
        lastRegularBin.features.push(feature);
      }
    }
  }

  // Only add N/A bin if it has features
  if (naBin.features.length > 0) {
    bins.push(naBin);
  }

  return bins;
}

export function binFeaturesByBinSize(
  features: GeomT[],
  property: string,
  lessThan?: number | null,
  binSize?: number | null,
  greaterThan?: number | null
): BinningResult {
  if (binSize == null || lessThan == null || greaterThan == null) {
    return { bins: [], method: "binSize", property };
  }

  // Allow binSize=0 for unique value binning, but reject near-zero values
  if (binSize !== 0 && Math.abs(binSize) <= EPSILON) {
    return { bins: [], method: "binSize", property };
  }

  const decimalPlaces = getDecimalPlaces(binSize);
  const isIntegerBinning = Number.isInteger(binSize);

  const values: number[] = [];
  let min = Infinity;
  let max = -Infinity;

  // Single pass numeric extraction + min/max
  for (const f of features) {
    const rawValue = f.Properties?.[property];
    if (isNAValue(rawValue)) {
      continue;
    }
    const v = Number(rawValue);
    if (!Number.isNaN(v)) {
      values.push(v);
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }

  if (!values.length) {
    return { bins: [], method: "binSize", property };
  }

  if (binSize === 0) {
    const map = new Map<number, Bin>();
    const naFeatures: GeomT[] = [];

    for (const value of values) {
      if (!map.has(value)) {
        map.set(value, {
          label: formatNumber(value),
          min: value,
          max: value,
          features: []
        });
      }
    }

    for (const f of features) {
      const rawValue = f.Properties?.[property];
      // Check for N/A values
      if (isNAValue(rawValue)) {
        naFeatures.push(f);
        continue;
      }
      const v = Number(rawValue);
      if (!Number.isNaN(v)) {
        map.get(v)?.features.push(f);
      } else {
        naFeatures.push(f);
      }
    }

    const bins = [...map.values()].map((b) => ({
      ...b,
      label: `${formatNumber(b.min)} (${b.features.length})`
    }));

    // Add n/a bin if there are features with non-numeric values
    if (naFeatures.length > 0) {
      bins.push({
        label: `n/a (${naFeatures.length})`,
        features: naFeatures,
        isNA: true
      });
    }

    return {
      bins,
      method: "binSize",
      property
    };
  }

  // Generate all possible bins
  const allBins = getAllPossibleBins(
    lessThan,
    binSize,
    greaterThan,
    isIntegerBinning,
    decimalPlaces
  );

  // Assign features to bins
  const binsWithFeatures = assignFeaturesToBins(features, property, allBins);

  // Add counts to labels for all bins
  const binsWithLabels = binsWithFeatures.map((bin) => ({
    ...bin,
    label: `${bin.label} (${bin.features.length})`
  }));

  return {
    bins: binsWithLabels,
    method: "binSize",
    property
  };
}

/**
 * Bin features using quantile method
 * Divides data into N equal-sized groups based on count distribution
 * Quantile binning ensures each bin has approximately the same number of features
 * Performance: O(n log n) due to sorting
 *
 * @param features - Array of features to bin
 * @param property - Property name to bin by
 * @param quantileCount - Number of quantiles (default: 4 for quartiles)
 */
export function binFeaturesByQuantile(
  features: GeomT[],
  property: string,
  quantileCount: number = 4
): BinningResult {
  const featuresWithValues: Array<{ feature: GeomT; value: number }> = [];
  const naFeatures: GeomT[] = [];

  for (const f of features) {
    const rawValue = f.Properties?.[property];
    if (isNAValue(rawValue)) {
      naFeatures.push(f);
      continue;
    }
    const value = parseFloat(String(rawValue));
    if (!isNaN(value)) {
      featuresWithValues.push({ feature: f, value });
    } else {
      naFeatures.push(f);
    }
  }

  // Sort numeric features
  featuresWithValues.sort((a, b) => a.value - b.value);

  if (featuresWithValues.length === 0) {
    // If all features are non-numeric, return just n/a bin
    if (naFeatures.length > 0) {
      return {
        bins: [
          {
            label: `n/a (${naFeatures.length})`,
            features: naFeatures,
            isNA: true
          }
        ],
        method: "quantile",
        property
      };
    }
    return { bins: [], method: "quantile", property };
  }

  const bins: Bin[] = [];
  const itemsPerBin = Math.floor(featuresWithValues.length / quantileCount);
  const remainder = featuresWithValues.length % quantileCount;

  let currentIndex = 0;

  // Create quantile bins with equal counts (distribute remainder across first bins)
  for (let i = 0; i < quantileCount; i++) {
    // Add 1 extra item to first 'remainder' bins to distribute items evenly
    const binSize = itemsPerBin + (i < remainder ? 1 : 0);

    if (currentIndex >= featuresWithValues.length) break;

    const binItems = featuresWithValues.slice(currentIndex, currentIndex + binSize);
    const binMin = binItems[0].value;
    const binMax = binItems[binItems.length - 1].value;

    bins.push({
      label: `${formatNumber(binMin)} - ${formatNumber(binMax)} (${binItems.length})`,
      min: binMin,
      max: binMax,
      features: binItems.map((item) => item.feature)
    });

    currentIndex += binSize;
  }
  if (naFeatures.length > 0) {
    bins.push({
      label: `n/a (${naFeatures.length})`,
      features: naFeatures,
      isNA: true
    });
  }

  return { bins, method: "quantile", property };
}
